import { SearchRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';
import { fShortenNumber } from 'src/utils/format-number';
import { HotSymbolCandidate } from '../types';
import { ChangePercent, PairName, SymbolAvatar, thinScrollbarSx } from './shared';

// 固定行高的轻量虚拟列表：候选池可能持续增长（当前 ~200，架构上可到千级），
// 只渲染可视区 + overscan 的行，无需引入额外依赖。
const ROW_HEIGHT = 60;
const OVERSCAN = 6;

interface CandidatesPanelProps {
  candidates: HotSymbolCandidate[];
  total: number;
  draftPins: string[];
  onPin: (symbol: string) => void;
}

interface RankedCandidate extends HotSymbolCandidate {
  // 全量列表中的成交量排名（搜索过滤后仍显示原排名）
  rank: number;
}

export default function CandidatesPanel({
  candidates,
  total,
  draftPins,
  onPin,
}: CandidatesPanelProps) {
  const translate = useTranslate();
  const [keyword, setKeyword] = useState('');
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(480);
  const listRef = useRef<HTMLDivElement>(null);

  // 置顶徽标必须按当前草稿实时计算，不能用接口返回的 isPinned/pinnedIndex（那是服务端已保存状态）
  const draftIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    draftPins.forEach((symbol, index) => map.set(symbol, index + 1));
    return map;
  }, [draftPins]);

  const ranked: RankedCandidate[] = useMemo(
    () => candidates.map((item, index) => ({ ...item, rank: index + 1 })),
    [candidates]
  );

  const filtered = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    if (!key) return ranked;
    return ranked.filter(
      (item) =>
        item.symbol.toLowerCase().includes(key) ||
        item.displaySymbol.toLowerCase().includes(key) ||
        (item.aliasName ?? '').toLowerCase().includes(key)
    );
  }, [ranked, keyword]);

  // 搜索条件变化时回到列表顶部，避免 scrollTop 停留在越界位置
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
    setScrollTop(0);
  }, [keyword]);

  // 跟踪可视高度（大小屏自适应）
  useEffect(() => {
    const el = listRef.current;
    if (!el) return undefined;
    const update = () => setViewportHeight(el.clientHeight || 480);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const endIndex = Math.min(
    filtered.length,
    Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT) + OVERSCAN
  );
  const visibleRows = filtered.slice(startIndex, endIndex);

  return (
    <Card sx={{ p: 3, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Typography variant="h6">{translate('module.hotSymbols.candidatesTitle')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        {translate('module.hotSymbols.candidatesHint')}
      </Typography>

      <TextField
        fullWidth
        size="small"
        value={keyword}
        onChange={(event) => setKeyword(event.target.value)}
        placeholder={translate('module.hotSymbols.searchPlaceholder')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRounded fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: 1.5, mb: 1 }}
      >
        <Typography variant="caption" color="text.secondary">
          {translate('module.hotSymbols.sortLabel')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {translate('module.hotSymbols.totalCount', { count: keyword ? filtered.length : total })}
        </Typography>
      </Stack>

      <Box
        ref={listRef}
        onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
        sx={(theme) => ({
          overflowY: 'auto',
          // 固定高度作为虚拟列表的可视区基准；栅格行默认等高拉伸，左侧卡片会跟随此高度
          height: { xs: 480, md: 600, xl: 720 },
          borderRadius: 1.5,
          ...thinScrollbarSx(theme),
        })}
      >
        {filtered.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {translate('module.hotSymbols.noResult')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ position: 'relative', height: filtered.length * ROW_HEIGHT }}>
            {visibleRows.map((item, index) => {
              const top = (startIndex + index) * ROW_HEIGHT;
              const draftIndex = draftIndexMap.get(item.symbol);
              return (
                <Stack
                  key={item.symbol}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{
                    position: 'absolute',
                    top,
                    left: 0,
                    right: 0,
                    height: ROW_HEIGHT,
                    px: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: draftIndex ? 'action.hover' : 'transparent',
                  }}
                >
                  {/* 已置顶行整体压暗（序号/头像/名称浅灰），标签用不可点击的浅灰样式 */}
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ flex: 1, minWidth: 0, opacity: draftIndex ? 0.55 : 1 }}
                  >
                    <Typography
                      variant="caption"
                      color={draftIndex ? 'text.disabled' : 'text.secondary'}
                      sx={{ width: 28, flexShrink: 0, textAlign: 'right' }}
                    >
                      {item.rank}
                    </Typography>
                    <SymbolAvatar symbol={item.symbol} label={item.displaySymbol} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <PairName displaySymbol={item.displaySymbol} dex={item.dex} />
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          {`$${fShortenNumber(item.volume)}`}
                        </Typography>
                        <ChangePercent value={item.changPxPercent} />
                      </Stack>
                    </Box>
                  </Stack>
                  {draftIndex ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={translate('module.hotSymbols.pinnedBadge', { index: draftIndex })}
                      sx={{ color: 'text.disabled', borderColor: 'divider' }}
                    />
                  ) : (
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      onClick={() => onPin(item.symbol)}
                    >
                      {translate('module.hotSymbols.pin')}
                    </Button>
                  )}
                </Stack>
              );
            })}
          </Box>
        )}
      </Box>
    </Card>
  );
}
